import { FormTemplatePickerDialog } from '@/components/FormTemplates/FormTemplatePickerDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import type { FormTemplateContract } from '@/contracts/form-templates'
import {
  createFormFromTemplate,
  listFormTemplates,
} from '@/lib/data-client/form-templates'
import { useAuth } from '@/lib/auth'
import { hasFormAdminAccess } from '@/lib/permissions'
import { useSystemFeatureVisibility } from '@/lib/system-feature-visibility'
import { formatRelativeTime } from '@/lib/utils'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Edit3, FileText, Plus, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

export default function FormTemplatesRoute() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const canManageForms = hasFormAdminAccess(user)
  const featureVisibility = useSystemFeatureVisibility()
  const templatesEnabled = featureVisibility['forms.templates']
  const [pickerOpen, setPickerOpen] = useState(false)
  const [creatingTemplateId, setCreatingTemplateId] = useState<string | null>(null)
  const { data: templatesResult, isLoading } = useQuery({
    queryKey: ['form-templates'],
    queryFn: listFormTemplates,
    enabled: canManageForms && templatesEnabled,
  })
  const templates = templatesResult?.data ?? []

  async function createFromSelectedTemplate(
    template: FormTemplateContract,
    input: { title?: string; description?: string },
  ) {
    setCreatingTemplateId(template.id)
    try {
      const form = await createFormFromTemplate(template.id, input)
      await queryClient.invalidateQueries({ queryKey: ['forms'] })
      setPickerOpen(false)
      toast.success('已使用範本建立問卷')
      await navigate({ to: '/dashboard/forms/$formId/edit', params: { formId: form.id } })
    } finally {
      setCreatingTemplateId(null)
    }
  }

  if (!canManageForms) {
    return (
      <div className="grid min-h-full place-items-center p-6">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle>沒有問卷範本管理權限</CardTitle>
          </CardHeader>
          <CardContent className="text-sm leading-6 text-slate-500">
            請使用管理員或超級管理員帳戶進入問卷範本管理。
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!templatesEnabled) {
    return (
      <div className="grid min-h-full place-items-center p-6">
        <Card className="w-full max-w-xl">
          <CardHeader>
            <CardTitle>問卷範本目前已隱藏</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm leading-6 text-slate-500">
            <p>此功能預設隱藏，需由超級管理員到「系統 / 問卷管理」開啟「問卷範本」。</p>
            <Button
              type="button"
              variant="outline"
              onClick={() => void navigate({ to: '/dashboard/system/forms' })}
            >
              前往功能顯示設定
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col p-4 lg:p-6">
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-950">問卷範本</h1>
          <p className="mt-1 text-sm text-slate-500">
            管理固定範本與可重用問卷範本，並用範本建立新的草稿問卷。
          </p>
        </div>
        <Button type="button" onClick={() => setPickerOpen(true)}>
          <Plus className="size-4" />
          使用範本建立問卷
        </Button>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {templates.map((template) => (
            <Card key={template.id} className="overflow-hidden">
              <CardHeader className="border-b border-slate-200">
                <div className="flex items-start gap-3">
                  <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-indigo-50 text-indigo-700">
                    {template.isSystem ? (
                      <Sparkles className="size-5" />
                    ) : (
                      <FileText className="size-5" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <CardTitle className="line-clamp-2 text-base">{template.title}</CardTitle>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {template.isSystem ? <Badge variant="secondary">固定範本</Badge> : null}
                      <Badge variant="outline">{template.category}</Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 p-4">
                <p className="line-clamp-3 min-h-16 text-sm leading-6 text-slate-500">
                  {template.description || '尚未填寫範本描述。'}
                </p>
                <div className="grid grid-cols-3 gap-2 text-center text-sm">
                  <div className="rounded-md bg-slate-50 p-2">
                    <div className="font-semibold text-slate-950">{template.questionCount}</div>
                    <div className="text-xs text-slate-500">題目</div>
                  </div>
                  <div className="rounded-md bg-slate-50 p-2">
                    <div className="font-semibold text-slate-950">{template.groupCount}</div>
                    <div className="text-xs text-slate-500">題組</div>
                  </div>
                  <div className="rounded-md bg-slate-50 p-2">
                    <div className="font-semibold text-slate-950">
                      {formatRelativeTime(template.updatedAt)}
                    </div>
                    <div className="text-xs text-slate-500">更新</div>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      void navigate({
                        to: '/dashboard/form-templates/$templateId/edit',
                        params: { templateId: template.id },
                      })
                    }
                  >
                    <Edit3 className="size-4" />
                    編輯
                  </Button>
                  <Button
                    type="button"
                    onClick={() => void createFromSelectedTemplate(template, {})}
                    disabled={creatingTemplateId === template.id}
                  >
                    <Plus className="size-4" />
                    建立問卷
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        {!isLoading && templates.length === 0 ? (
          <div className="grid min-h-96 place-items-center rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center">
            <div>
              <FileText className="mx-auto size-10 text-slate-400" />
              <h2 className="mt-4 font-semibold text-slate-950">尚未建立問卷範本</h2>
              <p className="mt-2 text-sm text-slate-500">
                可從問卷編輯器將目前問卷存成範本，或等待 D1 固定範本 seed 完成。
              </p>
            </div>
          </div>
        ) : null}
      </ScrollArea>

      <FormTemplatePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        templates={templates}
        isCreating={creatingTemplateId !== null}
        onCreateForm={createFromSelectedTemplate}
      />
    </div>
  )
}
